// Justificación de tamaño (policies/code.md, "SOLID"/"Component size") -- 799 líneas operativas.
// `ComponentTaskHoy.jsx` es el orquestador de la vista de tareas: ya pasó por 2 rondas reales de
// extracción (02.1-tasks.md §6-§13, 17 componentes hoja: `TaskListView`/`TaskKanbanView`/
// `TaskGanttView`/`TaskDetailPanel`/`TaskCommentsPanel`/`TaskDescriptionEditor`/`TaskSubtasks`/
// `TaskTagsRow`/`UserCell`+`UserSelectPopover`/`DurationPopover`/`CalendlyDatePopover`/
// `ReminderPopover`/`ShareTaskModal`/el árbol `comment-editor/`; y `02.1-b6-taskhoy-orchestrator.md`,
// 9 hooks nuevos: `useTaskPipeline`, `useTaskGanttInteraction`, `useTaskColumnResize`,
// `useTaskKanbanDrag`, `useTaskCellPopovers`, `useTaskSubtaskWizard`, `useTaskComments`,
// `useTaskRoadmapIntegration`, `useTaskDetailPortalTargets` + `useTaskCrud`). Bajó de 2814 a 799
// líneas operativas -- dentro del rango 500-800 que el usuario pidió explícitamente como cierre
// sano (2026-09-08), y el ejemplo sancionado en policies/code.md de "composición es en sí una
// responsabilidad legítima". Lo que queda es exactamente eso: el estado de vista derivado de la
// URL (`activeTab`/`timeRange`/`currentRange`/`activeTask`) que casi todos los subsistemas de
// arriba necesitan leer (extraerlo solo reubicaría el acoplamiento, no lo reduciría), los verbos
// de CRUD de tarea que cada vista/panel llama (`handleTaskClick`/`handleDeleteTask`/
// `handleCreateNewTask`/`updateTaskInDB`/`handleCheckboxChange`/`handleStageChange`), y la
// composición final del JSX (`TaskHoyToolbar`/`TaskGanttView`/`TaskListView`/`TaskKanbanView`/
// `ComponentTaskFlow`/`TaskDetailPanel` con sus render props). No separable más sin romper el
// mismo criterio que ya se usó para `ComponentTaskFlow.jsx` (ver su propio comentario).
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { GitMerge, BarChart3, Trophy, AlertCircle, ClipboardList, Eye, Users, ArrowRight, Trash2, Monitor, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { useTaskFilterUsers } from '../../../shared/hooks/useTaskFilterUsers';
import AccessGate from '../../spaces/components/AccessGate';
import { useCurrentUserPermissions } from '../../spaces/hooks/useSpaceDashboard';
import './ComponentTaskHoy.css';
import { ComponentTaskFlow } from './ComponentTaskFlow';
import { ShareTaskModal } from './ShareTaskModal';
import { TaskListView } from './TaskListView';
import { TaskKanbanView } from './TaskKanbanView';
import { TaskGanttView } from './TaskGanttView';
import { TaskTagsRow } from './TaskTagsRow';
import { TaskInfoContent } from './TaskInfoContent';
import { TaskCommentsPanel } from './TaskCommentsPanel';
import { TaskDetailPanel } from './TaskDetailPanel';
import { taskWorkframeSlots } from './workframe/taskWorkframeSlots';
import TaskWorkframePanel from './workframe/TaskWorkframePanel';
import WorkframeSharePromptModal from './workframe/WorkframeSharePromptModal';
import { useWorkframeDocSharing } from '../hooks/useWorkframeDocSharing';
import { FullScreenImageViewer } from './FullScreenImageViewer';
import { StandaloneTaskError } from './StandaloneTaskError';
import { TaskErrorBoundary } from './TaskErrorBoundary';
import { CalendarEventOverlay } from './CalendarEventOverlay';
import { SubtaskCreateWizard } from './SubtaskCreateWizard';
import { TaskHoyToolbar } from './TaskHoyToolbar';
import { useTaskPipeline } from '../hooks/useTaskPipeline';
import { useTaskListGrouping } from '../hooks/useTaskListGrouping';
import { useTaskColumnResize } from '../hooks/useTaskColumnResize';
import { useTaskKanbanDrag } from '../hooks/useTaskKanbanDrag';
import { useTaskGanttInteraction } from '../hooks/useTaskGanttInteraction';
import { useTaskCellPopovers } from '../hooks/useTaskCellPopovers';
import { useTaskSubtaskWizard } from '../hooks/useTaskSubtaskWizard';
import { useTaskComments } from '../hooks/useTaskComments';
import { useTaskRoadmapIntegration } from '../hooks/useTaskRoadmapIntegration';
import { useTaskDetailPortalTargets } from '../hooks/useTaskDetailPortalTargets';
import { useTaskCrud } from '../hooks/useTaskCrud';
import { useTaskViewPrefs } from '../hooks/useTaskViewPrefs';
import { ApiError } from '../../../shared/errors/ApiError';
import {
    useUpdateTask,
    useCreateTask,
    useDeleteTask,
    useUserSpaces,
    useTasksList,
    useStandaloneTask,
    useTasksCalendarEvents,
    useTasksSocketSync,
    useTasksCalendarEventsCache,
    useTaskSpaceName,
} from '../hooks/useComponentTaskData';
import { useAllUsers } from '../../../shared/hooks/useAllUsers';
import { useEscapeKey, useWindowEvent } from '../../../shared/hooks/useWindowEvent';
import { useTeamMembers } from '../../settings/hooks/useTeam';
import { toUserZoned } from '../../../shared/lib/tzDate';
import { TaskCommentEditorModals } from './TaskCommentEditorModals';
import {
    SELECTOR_STAGES,
    calculateDateRange, getStageColor,
    getGanttConfig,
    getTagColors,
} from '../lib/taskGrid';

// Referencia estable para el fallback de las queries de tareas/eventos (evita re-render por
// identidad nueva en cada render cuando la query aún no resolvió).
// Fase 5.18 (05-remediation.md, parte 2) -- `react-hooks/set-state-in-effect` (hint de React
// Compiler, no un bug de runtime) sube a `error` en todo `src/`. Este archivo hace setState
// síncrono dentro de un `useEffect` en un patrón de sincronización legítimo: cachea el nombre de espacio resuelto (ya documentado arriba, Fase 5.17) e hidrata `systemUsers` desde el query de usuarios.
// No se reescribe a estado derivado (repetiría el fetch/haría el UI menos predecible en
// varios de estos casos) -- deshabilitado a nivel de archivo, con motivo, no en silencio.
/* eslint-disable react-hooks/set-state-in-effect */
const EMPTY_TASKS = [];

// Fase 5.17 -- presentacional: `onNavigate` (antes `useNavigate`), `pathname` (antes
// `useLocation`), `taskIdParam` (antes `useParams().taskId`), `toast` y `onlyMyEvents` (antes
// `useToast`/`useSettings`) llegan por props de los containers (`TasksScreen` / `SpaceHomeScreen`
// / `StandaloneTaskScreen`). El sync de socket + la cache de eventos de calendario viven en
// `useComponentTaskData`.
const ComponentTaskHoy = ({ standaloneTaskId: propStandaloneTaskId, onCloseStandalone, standaloneMode, user, onPrevTask, onNextTask, onNavigate, pathname = '', taskIdParam, toast, onlyMyEvents = false }) => {
    const { permissions, isLoading: isPermissionsLoading } = useCurrentUserPermissions();
    const standaloneTaskId = standaloneMode ? taskIdParam : propStandaloneTaskId;
    const { t: translate } = useTranslation('tasks');
    const { taskFilterUsers } = useTaskFilterUsers(user?.dbId);
    // Fase timezones (sevilla-timezones): toda la matemática de la grilla (posiciones, drag,
    // navegación de fecha) usa este timezone, nunca el del navegador -- ver shared/lib/tzDate.js.
    const userTz = user?.timezone || 'America/Mexico_City';
    // "Hoy" en formato YYYY-MM-DD, calculado en `userTz` -- reemplaza varios defaults que estaban
    // hardcodeados a '2026-06-01' (leftover de una sesión de pruebas), usados en paths reales de
    // guardado (ej. handleDrop de la grilla "hoy" guardaba `date: '2026-06-01'` siempre que el
    // usuario nunca hubiera abierto el popover de calendario que sí actualiza ese estado).
    const todayStr = (() => {
        const z = toUserZoned(new Date(), userTz);
        const pad = (n) => String(n).padStart(2, '0');
        return `${z.getFullYear()}-${pad(z.getMonth() + 1)}-${pad(z.getDate())}`;
    })();
    const navigate = onNavigate;

    const updateTaskMutation = useUpdateTask();
    const createTaskMutation = useCreateTask();
    const deleteTaskMutation = useDeleteTask();

    // Task panel overlay states
    const [createSpaceDropdownOpen, setCreateSpaceDropdownOpen] = useState(false);
    const titleTimeoutRef = useRef(null);
    useEffect(() => () => {
        if (titleTimeoutRef.current) clearTimeout(titleTimeoutRef.current);
    }, []);
    const [newTagInput, setNewTagInput] = useState('');
    const [fullScreenImage, setFullScreenImage] = useState(null);
    const [isTagsCollapsed, setIsTagsCollapsed] = useState(false);

    // 04-enforce.md 4.3 -- suscripción `keydown` compartida (Escape cierra el visor de imagen).
    useEscapeKey(() => setFullScreenImage(null), !!fullScreenImage);

    // Dynamic Date -- en `userTz`, no en el navegador.
    const getFormattedDate = () => {
        const d = new Date();
        const options = { weekday: 'long', day: 'numeric', month: 'long', timeZone: userTz };
        return d.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-ES', options);
    };

    // Parse URL for active view and filter
    const decodedPathname = decodeURIComponent(pathname);
    const pathSegments = decodedPathname.split('/');
    // Format: /:spaceSlug/task/:viewMode/:filter -> e.g. /frqtal/task/list/today
    const spaceSlug = pathSegments[1] || '';
    const isWorkframeRoute = decodedPathname.includes('/workframe');
    // `user.currentSpace` ya lo mantiene sincronizado con la URL el SpaceLayout de App.jsx
    // (bloquea el render hasta resolverlo) -- no hay que re-derivarlo acá. Un intento anterior
    // de hacerlo leyendo `user.spaces` dejaba `activeSpaceId` siempre en null (ese campo nunca
    // se popula en ningún lado, ni frontend ni backend), lo que rompía el space_id de toda
    // tarea/mención nueva creada hoy mismo.
    const activeSpaceId = user?.currentSpace?.id || null;
    
    // Detect active task from URL subpath (moved up to fetch missing task in parallel)
    const lastSegment = pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : '';
    const activeTaskIdFromUrl = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lastSegment) ? lastSegment : null;
    const activeTaskId = standaloneTaskId || activeTaskIdFromUrl;

    // Fetch team members for the effective space
    const standaloneTaskQuery = useStandaloneTask(activeTaskId);
    const effectiveSpaceId = (standaloneMode || propStandaloneTaskId) ? (standaloneTaskQuery.data?.[0]?.space_id || activeSpaceId) : activeSpaceId;
    const { data: spaceMembersData = [] } = useTeamMembers(effectiveSpaceId);

    // Fetch user spaces for the "all" dashboard creation dropdown
    const { data: userSpaces = [] } = useUserSpaces(user?.id);

    const getTaskPermissions = useCallback((taskSpaceId) => {
        if (taskSpaceId && activeSpaceId && String(activeSpaceId) !== String(taskSpaceId)) {
            const space = userSpaces.find(s => String(s.id) === String(taskSpaceId));
            if (space && space.permissions) return space.permissions;
        }
        return permissions || {};
    }, [activeSpaceId, userSpaces, permissions]);

    const viewModeSegment = pathSegments[3] || 'list';
    const activeTab = viewModeSegment === 'roadmap' ? 'flow' : 
                      viewModeSegment === 'kanban' ? 'tablero' : 'lista';

    // timeFilter maps to timeRange
    const timeFilter = pathSegments[4] || 'today';
    // Map URL timeFilter to internal timeRange names
    const timeRange = timeFilter === 'today' ? 'hoy' :
                      timeFilter === 'week' ? 'week' :
                      timeFilter === 'month' ? 'month' :
                      timeFilter === '3months' ? '3_months' :
                      timeFilter === '6months' ? '6_months' :
                      timeFilter === '1year' ? '1_year' :
                      timeFilter === 'custom' ? 'custom' : 'hoy';

    // Bug (2026-07-12): abrir una tarea navegaba a `/task/<uuid>` reemplazando la URL entera —
    // perdía el espacio/vista/rango activos (`spaceSlug`/`viewModeSegment`/`timeFilter` volvían
    // a sus defaults 'proyecto'/'list'/'today' al recalcularse desde una URL de solo 3 segmentos).
    // Cualquier refetch/interval posterior volvía a pedir tareas con el filtro forzado a "hoy"
    // en vez del que el usuario tenía elegido (p. ej. "sem"), y si la tarea abierta no caía
    // dentro de "hoy" quedaba fuera de `tasks`, así que `activeTask` se volvía `undefined` y el
    // modal se cerraba solo. `basePath` preserva el contexto: abrir agrega el id de la tarea al
    // final de la URL actual, cerrar vuelve a este mismo `basePath` sin el id.
    const basePath = `/${spaceSlug}/task/${viewModeSegment}/${timeFilter}`;
    
    // Si viene un workframe en la URL como parámetro flotante
    const queryWorkframeId = new URLSearchParams(window.location.search).get('workframe');

    // Dummy setters that trigger navigation instead of state mutation
    const { saveViewPref, saveTimePref } = useTaskViewPrefs(user?.db_id);

    const setActiveTab = (tab) => {
        saveViewPref(tab);
        toast.success(`Vista guardada`);
        const viewMode = tab === 'flow' ? 'roadmap' : tab === 'tablero' ? 'kanban' : 'list';
        navigate(`/${spaceSlug}/task/${viewMode}/${timeFilter}`);
    };
    
    const setTimeRange = (range) => {
        const urlRange = range === 'hoy' ? 'today' :
                         range === 'week' ? 'week' :
                         range === 'month' ? 'month' :
                         range === '3_months' ? '3months' :
                         range === '6_months' ? '6months' :
                         range === '1_year' ? '1year' :
                         range === 'custom' ? 'custom' : 'today';
        saveTimePref(urlRange);
        toast.success(`Filtro guardado`);
        navigate(`/${spaceSlug}/task/${viewModeSegment}/${urlRange}`);
    };

    // `tasks`/`calendarEvents`/`reminderEvents` se derivan de `useQuery` más abajo (paso a2).
    const handleTaskClick = (task, e) => {
        if (e) e.stopPropagation();
        if (task.is_calendar_event) {
            const evId = task.originalEventId || task.original_id || task.id;
            const fullEvent = calendarEvents.find(ce => String(ce.id) === String(evId) || String(ce.id) === String(evId).split('_')[0]);
            setActiveCalendarEvent(fullEvent || task);
        } else if (task.stage !== 'recordatorio') {
            navigate(`${basePath}/${task.original_id || task.id}`);
        }
    };

    // `loading` — ver el latch derivado más abajo, junto a `listLoadSettled`.
    const hasLoadedOnceRef = useRef(false);


    // slice 02.1 · paso a2 — `tasks`/`calendarEvents` se leen DIRECTO de `useQuery` (ya no hay
    // `useState` espejo). La escritura optimista vive en `useUpdateTask` (`onMutate` sobre la
    // cache) + rollback en `onError`; el socket `task_*` y los `invalidateQueries` reconcilian
    // con el servidor. Ver `02.1-tasks.md` §6 / §3. `reminderEvents` se dejó de derivar: el
    // `useState` que existía se escribía pero nada en el render lo leía.
    const [dateOffset, setDateOffset] = useState(0);
    // `calculateDateRange` (nivel de módulo) es pura — memoizada para identidad estable.
    const currentRange = useMemo(() => calculateDateRange(timeRange, dateOffset, userTz), [timeRange, dateOffset, userTz]);

    const tasksListQuery = useTasksList({
        activeSpaceId, timeRange, currentRange, dateOffset,
        enabled: !standaloneTaskId,
    });
    const tasksCalendarEventsQuery = useTasksCalendarEvents({
        activeSpaceId, currentRange, onlyMyEvents, userId: user?.id, userNick: user?.name,
        enabled: !standaloneTaskId,
    });

    const calendarEvents = tasksCalendarEventsQuery.data?.calendarEvents ?? EMPTY_TASKS;

    // Active task detection moved up

    const taskFromList = tasksListQuery.data?.find(t => t.id === activeTaskId);
    const missingTaskId = activeTaskId && !taskFromList ? activeTaskId : null;

    // Renderizamos la lista principal intacta, pero le inyectamos la tarea abierta si quedó fuera
    // de los filtros de fecha para que los sub-componentes (subtareas, etc) la encuentren.
    const tasks = (standaloneTaskId || missingTaskId 
        ? [...(tasksListQuery.data || []), ...(standaloneTaskQuery.data || [])] 
        : tasksListQuery.data) ?? EMPTY_TASKS;

    const activeTask = tasks.find(t => t.id === activeTaskId);

    // Overlay de evento de calendario (abierto desde `handleTaskClick` arriba) + autocomplete de
    // roadmap/triggers + drag&drop/resize sobre el roadmap -- extraído a
    // `useTaskRoadmapIntegration.js` (no contemplado en las fases A-E originales del plan,
    // encontrado al re-medir tras cerrar D1+E1).
    const {
        activeCalendarEvent, setActiveCalendarEvent,
        handleCloseCalendarEvent, handleSaveCalendarEvent, handleDeleteCalendarEvent,
        roadmapSearchQuery, setRoadmapSearchQuery,
        showRoadmapSearch, setShowRoadmapSearch,
        showRoadmapTriggers, setShowRoadmapTriggers,
        focusedRoadmapTaskIdx, setFocusedRoadmapTaskIdx,
        roadmapSearchTasks, flowRef, handleRoadmapTaskSelect,
        triggerSearchQuery, setTriggerSearchQuery,
        showTriggerSearch, setShowTriggerSearch,
        focusedTriggerIdx, setFocusedTriggerIdx,
        availableTriggers, setAvailableTriggers,
        triggerSearchTasks, handleTriggerSelect,
        handleRoadmapTaskDrop, handleRoadmapTaskResize,
    } = useTaskRoadmapIntegration({ tasks, activeTab, userTz, toast, translate, updateTaskMutation });

    const taskSubscribers = activeTask?.subscribers || [];

    // `CollaborativeEditor` (Yjs) es la descripción; tiene su propia toolbar + drop/paste. El
    // subsistema viejo (`descriptionEditorRef`/LilMenu/undo/`tempDesc`) era del contenteditable
    // crudo pre-Yjs y estaba inerte (la ref nunca se attacheaba) — borrado en b.5.0. El editor
    // + su debounce + la barra de subida viven en `TaskDescriptionEditor` (b.5b) — sin estado
    // `initialDescHtml`, `initialHtml` entra directo de `activeTask.description`.

    const [isNarrowScreen, setIsNarrowScreen] = useState(window.innerWidth < 850);

    // Pagination (increased to 100 items per page)
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 100;

    // Integración main→dev, epic C2: agrupación por etapa colapsable + toggle "ocultar
    // completadas" en la vista de LISTA (estado persistido). Se pasa entero con spread al
    // pipeline y a `TaskListView`.
    const listGrouping = useTaskListGrouping();

    // Options Menu States
    const [activeTaskDropdownId, setActiveTaskDropdownId] = useState(null);
    const [deletingTaskId, setDeletingTaskId] = useState(null);
    
    // UI Flash States
    const [dateFlash, setDateFlash] = useState(false);
    const prevStartTimeRef = useRef(activeTask?.start_time);

    useEffect(() => {
        if (activeTask && activeTask.start_time !== prevStartTimeRef.current) {
            setDateFlash(true);
            const timer = setTimeout(() => setDateFlash(false), 2000);
            prevStartTimeRef.current = activeTask.start_time;
            return () => clearTimeout(timer);
        }
    }, [activeTask?.start_time]);

    // Calendar States
    const [selectedCalendarDay, setSelectedCalendarDay] = useState(todayStr);

    // Filter Navigation States
    // timeRange/setTimeRange se derivan de la URL arriba; `dateOffset`/`currentRange` se
    // movieron antes de `activeTask` (las queries de tareas los necesitan) — paso a2.

    const [spaceNameCache, setSpaceNameCache] = useState({});
    // Modo standalone (`/t/:taskId`): tarea inexistente o sin permiso de space -- `activeTask`
    // se queda undefined en ambos casos; `standaloneError` (derivado más abajo del `error` de
    // la query) evita dejar la pantalla en blanco.
    const [shareModalOpen, setShareModalOpen] = useState(false);

    // Flow State: hovered task ID for highlighting connection paths
    const [hoveredFlowNodeId, setHoveredFlowNodeId] = useState(null);

    // Search Query
    const [searchQuery, setSearchQuery] = useState('');

    // HACK: Override parent (Menu2) scroll based on timeRange
    useEffect(() => {
        const menu2ScrollContainer = document.querySelector('.menu2-doc-panel > div');
        if (menu2ScrollContainer) {
            menu2ScrollContainer.style.overflowY = 'auto';
        }
        return () => {
            if (menu2ScrollContainer) menu2ScrollContainer.style.overflowY = 'auto';
        };
    }, [timeRange]);

    // Autocomplete User Select States
    const [systemUsers, setSystemUsers] = useState([]);
    const { data: fetchedSystemUsers } = useAllUsers();
    useEffect(() => {
        if (fetchedSystemUsers) setSystemUsers(fetchedSystemUsers);
    }, [fetchedSystemUsers]);

    // Combine users to show: Prioritize space members at the top, followed by all workspace users
    const usersToSelectFrom = React.useMemo(() => {
        const spaceUsers = (effectiveSpaceId && Array.isArray(spaceMembersData))
            ? spaceMembersData.map(m => ({ ...m, nick: m.nick || m.name }))
            : [];

        const allUsers = (fetchedSystemUsers && fetchedSystemUsers.length > 0)
            ? fetchedSystemUsers
            : systemUsers;

        if (spaceUsers.length > 0) {
            const spaceUserIds = new Set(spaceUsers.map(u => String(u.id ?? u.dbId ?? u.uuid)));
            const spaceUserNicks = new Set(spaceUsers.map(u => (u.nick || u.name || '').toLowerCase()).filter(Boolean));
            const otherUsers = (allUsers || []).filter(u => {
                const uid = String(u.id ?? u.dbId ?? u.uuid);
                const nick = (u.nick || u.name || '').toLowerCase();
                return !spaceUserIds.has(uid) && (!nick || !spaceUserNicks.has(nick));
            });
            return [...spaceUsers, ...otherUsers];
        }

        return allUsers || [];
    }, [effectiveSpaceId, spaceMembersData, systemUsers, fetchedSystemUsers]);

    // Wizard de creación de subtareas -- extraído a `useTaskSubtaskWizard.js`
    // (02.1-b6-taskhoy-orchestrator.md, paso D3). El JSX presentacional vive en
    // `SubtaskCreateWizard.jsx` desde la Fase A.
    const {
        subtaskForm, newSubtaskTitle, setNewSubtaskTitle,
        subtaskInputState, setSubtaskInputState,
        subtaskFilteredUsers, setSubtaskFilteredUsers,
        subtaskSelectedUserIndex, setSubtaskSelectedUserIndex,
        handleCreateSubtask, handleSubtaskKeyDown,
    } = useTaskSubtaskWizard({ activeTask, systemUsers, user, activeSpaceId, createTaskMutation });

    // Panel de comentarios completo (data+mutaciones+socket+drafts del editor+tabs) + wizard de
    // recordatorio -- extraído a `useTaskComments.jsx` (02.1-b6-taskhoy-orchestrator.md, pasos
    // D1+E1, combinados por la entrelazadura entre `onSaveReminder`/`submitTaskComment`).
    const {
        taskComments, taskFilteredComments,
        commentsListRef,
        taskCommentsTab, setTaskCommentsTab,
        editingCommentId, setEditingCommentId,
        expandedCommentEditor, setExpandedCommentEditor,
        createDraftContent, setCreateDraftContent,
        createResetKey,
        editDraftContent, setEditDraftContent,
        taskSmartTags, setTaskSmartTags,
        toggleCommentReaction, deleteComment, saveEditedComment, handleToggleMediaVisibility,
        submitTaskComment,
        suggestionMode, setSuggestionMode, enterSuggestionMode,
        taskMode, setTaskMode, enterTaskMode,

        setReminderPopoverPos,
        showReminderPopover, setShowReminderPopover,
        activeReminderCommentId, setActiveReminderCommentId,
        pendingReminder, renderReminderPopover,

        hoveredReminderId, hoveredReminderData, reminderTooltipPos,
        handleReminderMouseEnter, handleReminderMouseLeave, handleReminderClick,
    } = useTaskComments({ activeTask, user, userTz, systemUsers, translate });

    // Compartir el documento estructurado al mencionar a alguien sin acceso (epic D1.4).
    const workframeDocSharing = useWorkframeDocSharing({ activeTask });

    // 04-enforce.md 4.3 -- `resize` por la suscripción compartida en vez de un listener propio.
    useWindowEvent('resize', () => {
        const narrow = window.innerWidth < 850;
        if (narrow === isNarrowScreen) return;
        setIsNarrowScreen(narrow);
        if (narrow) {
            setIsTagsCollapsed(true);
            setTaskCommentsTab('info');
        } else {
            setIsTagsCollapsed(false);
            if (taskCommentsTab === 'info') setTaskCommentsTab('comentarios');
        }
    });

    // Resize de columnas de TaskListView -- extraído a `useTaskColumnResize.js`
    // (02.1-b6-taskhoy-orchestrator.md, paso C2).
    const {
        columnWidths, resizingCol,
        handleColResizeStart, handleColResizeMove, handleColResizeEnd,
    } = useTaskColumnResize({ userId: user?.id });

    const handleAddTag = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const val = e.target.value.trim();
            if (!val) return;
            
            const currentTags = activeTask.tags ? activeTask.tags.split(',').map(s => s.trim()).filter(Boolean) : [];
            if (!currentTags.includes(val)) {
                const newTagsList = [...currentTags, val].join(',');
                updateTaskInDB(activeTask.id, { tags: newTagsList });
            }
            e.target.value = '';
        }
    };

    const handleRemoveTag = (tagToRemove) => {
        const currentTags = activeTask.tags ? activeTask.tags.split(',').map(s => s.trim()).filter(Boolean) : [];
        const newTagsList = currentTags.filter(t => t !== tagToRemove).join(',');
        updateTaskInDB(activeTask.id, { tags: newTagsList });
    };

    const MONTH_NAMES = [
        "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", 
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];

    // CRUD de tareas: sync de socket invalidation-first (Fase 5.17: movido a
    // `useTasksSocketSync`).
    useTasksSocketSync();

    // Nombre del espacio de la tarea activa (Fase 5.17: `apiClient.get` inline → `useTaskSpaceName`).
    const { data: fetchedSpaceName } = useTaskSpaceName(activeTask?.space_id);
    useEffect(() => {
        if (activeTask?.space_id && fetchedSpaceName && !spaceNameCache[activeTask.space_id]) {
            setSpaceNameCache((prev) => ({ ...prev, [activeTask.space_id]: fetchedSpaceName }));
        }
    }, [activeTask?.space_id, fetchedSpaceName, spaceNameCache]);

    // `403 code:3005` (no-miembro del espacio) → pantalla de no-miembro (solo se pinta en
    // `standaloneMode`, igual que antes). Derivado del `error` de la query, sin estado.
    const standaloneError = useMemo(() => {
        const err = standaloneTaskId ? standaloneTaskQuery.error : null;
        if (!err) return null;
        if (err instanceof ApiError && err.status === 403 && err.body?.code === 3005) {
            return { type: 'forbidden', spaceName: err.body.space_name };
        }
        return { type: 'generic' };
    }, [standaloneTaskId, standaloneTaskQuery.error]);

    // Escritura optimista sobre la cache de eventos de calendario de la vista de tareas
    // (feedback de drag/resize); el `invalidateQueries` que corre tras la escritura al servidor
    // reconcilia con la verdad. Fase 5.17: movido a `useTasksCalendarEventsCache`.
    const { patchEvent: patchTasksCalendarEvent, invalidateEvents: invalidateTasksCalendarEvents } = useTasksCalendarEventsCache();

    // slice 02.1 · c.1 — `loading` engancha a `false` una sola vez tras la primera carga y se
    // queda ahí (los cambios de rango posteriores muestran la lista vieja, no "cargando" de
    // nuevo). Antes era `useState` + `useEffect` (un render extra sólo para bajar la bandera);
    // derivado inline con un ref escrito durante el render es el mismo comportamiento sin el
    // round-trip (`rerender-derived-state-no-effect`).
    const listLoadSettled = standaloneTaskId
        ? !standaloneTaskQuery.isPending
        : (!tasksListQuery.isPending && !tasksCalendarEventsQuery.isPending);
    if (listLoadSettled) hasLoadedOnceRef.current = true;
    const loading = !hasLoadedOnceRef.current;

    // System users loaded above

    // Nodos de portal del panel de detalle -- extraído a `useTaskDetailPortalTargets.js`.
    const { portalTarget, bottomLeftTarget } = useTaskDetailPortalTargets({
        activeTask, standaloneTaskId, standaloneMode,
    });

    // Pipeline de tareas -- extraído a `useTaskPipeline.js` (02.1-b6-taskhoy-orchestrator.md,
    // paso B1): derivación pura, sin refs de DOM, sin efectos. `...listGrouping` (epic C2)
    // aporta `collapsedStages`/`showCompletedTasks` para la agrupación por etapa de la lista.
    const { standardTasks, taskMetrics, ganttTasks, currentTasks, totalPages } = useTaskPipeline({
        tasks, calendarEvents, currentRange, userTz, timeRange, dateOffset, searchQuery,
        taskFilterUsers, systemUsers, user, activeTab, currentPage, itemsPerPage, todayStr, translate, ...listGrouping,
    });

    // Los 3 verbos de persistencia de tareas (borrar/crear/actualizar) -- extraídos a
    // `useTaskCrud.js`.
    const { handleDeleteTask, handleCreateNewTask, updateTaskInDB, createWorkframeUpdate } = useTaskCrud({
        deleteTaskMutation, createTaskMutation, updateTaskMutation,
        toast, translate, navigate, activeSpaceId, activeTab, todayStr, user, basePath,
        setDeletingTaskId, setActiveTaskDropdownId,
    });

    // Motor de interacción del Gantt (drag & drop + resize + línea de "hora actual") --
    // extraído a `useTaskGanttInteraction.js` (02.1-b6-taskhoy-orchestrator.md, paso C1).
    const {
        ganttContainerRef, dragOverLeft, currentTimeOffset, isResizing,
        handleDragOver, handleDrop, handleGanttRowDragOver, handleGanttRowDrop,
        handleDragStart, handleDragEnd,
        handleResizeStart, handleResizeMove, handleResizeEnd,
    } = useTaskGanttInteraction({
        timeRange, currentRange, userTz, tasks, calendarEvents, ganttTasks, selectedCalendarDay,
        toast, updateTaskInDB, patchTasksCalendarEvent, invalidateTasksCalendarEvents,
    });

    // Popovers de celda (Calendly, duración, selección de usuario) -- extraído a
    // `useTaskCellPopovers.jsx` (02.1-b6-taskhoy-orchestrator.md, pasos D2+D4+D5).
    const {
        activeCalendlyPopoverTaskId, handleOpenCalendlyPopover, renderCalendlyPopover,
        popoverDurationVal, setPopoverDurationVal, popoverDate, setPopoverDate,
        popoverMonth, popoverYear, handlePrevMonth, handleNextMonth, handleSaveCalendlyDetails,
        dateConflictTaskId, dateConflictMessage,

        activeDurationPopoverTaskId, setActiveDurationPopoverTaskId, durationPopoverRef,
        renderDurationPopover, customTimeNum, customTimeUnit,
        setCustomTimeNum, setCustomTimeUnit, handleCustomTimeSubmit, handleTimeSelect,

        activeUserSelectTaskId, activeUserSelectField, userSearchQuery, setUserSearchQuery,
        focusedUserOptionIdx, setFocusedUserOptionIdx, userSelectPopoverRef,
        handleOpenUserSelect, handleToggleUserSelect, renderUserCell, renderUserSelectPopover,
    } = useTaskCellPopovers({
        userTz, todayStr, systemUsers, usersToSelectFrom, updateTaskInDB,
        tasks, selectedCalendarDay, setSelectedCalendarDay,
    });

    // Checkbox Change Handler
    const handleCheckboxChange = (id, currentVal) => {
        const task = tasks.find(t => t.id === id);
        if (task && getTaskPermissions(task.space_id)?.task_edit === false) return;
        const newVal = !currentVal;
        // Optimistic UI update
        updateTaskInDB(id, { checked: newVal, stage: newVal ? 'done' : 'en proceso' });
    };

    // Stage Change Handler
    const handleStageChange = (id, newStage) => {
        const task = tasks.find(t => t.id === id);
        if (task && getTaskPermissions(task.space_id)?.task_edit === false) return;
        const isChecked = newStage === 'done';
        updateTaskInDB(id, { stage: newStage, checked: isChecked });
    };

    // Drag & drop del tablero Kanban -- extraído a `useTaskKanbanDrag.js`
    // (02.1-b6-taskhoy-orchestrator.md, paso C3).
    const {
        kanbanScrollRef,
        handleKanbanDragStart, handleKanbanDragOver, handleKanbanDragEnd, handleKanbanDrop,
    } = useTaskKanbanDrag({ tasks, getTaskPermissions, handleStageChange });



    // slice 02.1 · b.5a — booleanos de permiso para `TaskTagsRow` (nunca pasar `getTaskPermissions`
    // en sí, ver `02.1-b5-detail-panel.md` §2).
    const canEditActiveTask = getTaskPermissions(activeTask?.space_id)?.task_edit !== false;
    const canRescheduleActiveTask = getTaskPermissions(activeTask?.space_id)?.task_reschedule !== false;
    const canDeleteActiveTask = getTaskPermissions(activeTask?.space_id)?.task_delete !== false;
    const activeTaskSpaceName = activeTask?.space_id
        ? (spaceNameCache[activeTask.space_id] || (Array.isArray(user?.spaces) ? (user.spaces.find(s => String(s.id) === String(activeTask.space_id))?.name || 'ESPACIO') : 'ESPACIO'))
        : null;
    const activeTaskParent = activeTask?.parent_task_id ? tasks.find(t => t.id === activeTask.parent_task_id) : null;
    // `parent_task_id` reemplaza el viejo `activeTask.subtasks[]`.
    const activeTaskSubtasks = activeTask ? tasks.filter(t => t.parent_task_id === activeTask.id) : [];

    const renderInfoContent = () => (
        <TaskInfoContent
            taskId={activeTask?.id}
            initialDescriptionHtml={activeTask?.description}
            userId={user?.id}
            currentUser={user}
            isDatePopoverOpen={!!activeCalendlyPopoverTaskId}
            onSaveDescription={async (html) => {
                const res = await updateTaskInDB(activeTask.id, { description: html });
                if (res.success) toast.success(`Descripción de la tarea guardada`);
                else toast.error(`Error al guardar: ${activeTask.title || 'Tarea'}`);
            }}
            parentTask={activeTaskParent}
            systemUsers={systemUsers}
            usersToSelectFrom={usersToSelectFrom}
            onOpenTask={(id) => navigate(`${basePath}/${id}`)}
            subtasks={activeTaskSubtasks}
            subtaskForm={
                <SubtaskCreateWizard
                    canCreate={getTaskPermissions(activeTask.space_id)?.task_create !== false}
                    subtaskInputState={subtaskInputState}
                    subtaskFilteredUsers={subtaskFilteredUsers}
                    subtaskSelectedUserIndex={subtaskSelectedUserIndex}
                    subtaskForm={subtaskForm}
                    newSubtaskTitle={newSubtaskTitle}
                    systemUsers={systemUsers}
                    setSubtaskInputState={setSubtaskInputState}
                    setNewSubtaskTitle={setNewSubtaskTitle}
                    setSubtaskFilteredUsers={setSubtaskFilteredUsers}
                    setSubtaskSelectedUserIndex={setSubtaskSelectedUserIndex}
                    handleCreateSubtask={handleCreateSubtask}
                    handleSubtaskKeyDown={handleSubtaskKeyDown}
                />
            }
        />
    );

    const currentGanttConfig = getGanttConfig(timeRange, currentRange.startStr, currentRange.endStr);

    return (
        <AccessGate permissionKey="task_view" defaultVal={true}>
        <>
            {!standaloneTaskId && (
                <div className="task-hoy-panel" style={{ overflowY: 'auto' }}>
                    {/* Pestañas (Tabs) colocadas arriba de los recuadros con Buscador */}
            <TaskHoyToolbar
                isWorkframeRoute={isWorkframeRoute}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                timeRange={timeRange}
                setTimeRange={setTimeRange}
                dateOffset={dateOffset}
                setDateOffset={setDateOffset}
                currentRange={currentRange}
                triggerSearchQuery={triggerSearchQuery}
                setTriggerSearchQuery={setTriggerSearchQuery}
                showTriggerSearch={showTriggerSearch}
                setShowTriggerSearch={setShowTriggerSearch}
                focusedTriggerIdx={focusedTriggerIdx}
                setFocusedTriggerIdx={setFocusedTriggerIdx}
                triggerSearchTasks={triggerSearchTasks}
                handleTriggerSelect={handleTriggerSelect}
                roadmapSearchQuery={roadmapSearchQuery}
                setRoadmapSearchQuery={setRoadmapSearchQuery}
                showRoadmapSearch={showRoadmapSearch}
                setShowRoadmapSearch={setShowRoadmapSearch}
                focusedRoadmapTaskIdx={focusedRoadmapTaskIdx}
                setFocusedRoadmapTaskIdx={setFocusedRoadmapTaskIdx}
                roadmapSearchTasks={roadmapSearchTasks}
                handleRoadmapTaskSelect={handleRoadmapTaskSelect}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                showRoadmapTriggers={showRoadmapTriggers}
                setShowRoadmapTriggers={setShowRoadmapTriggers}
                activeSpaceId={activeSpaceId}
                userSpaces={userSpaces}
                createSpaceDropdownOpen={createSpaceDropdownOpen}
                setCreateSpaceDropdownOpen={setCreateSpaceDropdownOpen}
                handleCreateNewTask={handleCreateNewTask}
                getTaskPermissions={getTaskPermissions}
                {...listGrouping}
            />

            {/* Línea de Tiempo Gantt Horizontal */}
            {timeRange === 'hoy' && activeTab === 'lista' && (
            <TaskGanttView
                containerRef={ganttContainerRef}
                ganttConfig={currentGanttConfig}
                ganttTasks={ganttTasks}
                loading={loading}
                startStr={currentRange.startStr}
                userTz={userTz}
                systemUsers={systemUsers}
                taskFilterUsers={taskFilterUsers}
                activeSpaceId={activeSpaceId}
                spaceMembersData={spaceMembersData}
                dragOverLeft={dragOverLeft}
                currentTimeOffset={currentTimeOffset}
                isResizing={isResizing}
                onHeaderDragOver={handleDragOver}
                onHeaderDrop={handleDrop}
                onRowDragOver={handleGanttRowDragOver}
                onRowDrop={handleGanttRowDrop}
                onBarDragStart={handleDragStart}
                onBarDragEnd={handleDragEnd}
                onResizeStart={handleResizeStart}
                onResizeMove={handleResizeMove}
                onResizeEnd={handleResizeEnd}
                onBarClick={handleTaskClick}
            />
            )}

            {/* Contenido de la pestaña activa (inmediatamente debajo de la línea de tiempo a unos 5px) */}
            <div className={`active-tab-content-wrapper ${activeTab === 'flow' ? 'is-flow' : ''}`}>
                {loading ? (
                    <div className="tab-loading">{translate('empty.loadingTasks')}</div>
                ) : activeTab === 'lista' ? (
                    <TaskListView
                        tasks={currentTasks}
                        userTz={userTz}
                        columnWidths={columnWidths}
                        resizingCol={resizingCol}
                        currentPage={currentPage}
                        totalPages={totalPages} {...listGrouping}
                        getTaskPermissions={getTaskPermissions}
                        activeCalendlyPopoverTaskId={activeCalendlyPopoverTaskId}
                        activeDurationPopoverTaskId={activeDurationPopoverTaskId}
                        activeUserSelectTaskId={activeUserSelectTaskId}
                        activeUserSelectField={activeUserSelectField}
                        activeTaskDropdownId={activeTaskDropdownId}
                        deletingTaskId={deletingTaskId}
                        onColResizeStart={handleColResizeStart}
                        onColResizeMove={handleColResizeMove}
                        onColResizeEnd={handleColResizeEnd}
                        onRowDragStart={handleDragStart}
                        onCheckboxChange={handleCheckboxChange}
                        onStageChange={handleStageChange}
                        onTitleClick={handleTaskClick}
                        onPriorityChange={(id, p) => updateTaskInDB(id, { priority: p })}
                        onOpenCalendly={handleOpenCalendlyPopover}
                        onOpenDuration={setActiveDurationPopoverTaskId}
                        onToggleDropdown={(id) => { setActiveTaskDropdownId(activeTaskDropdownId === id ? null : id); setDeletingTaskId(null); }}
                        onStartDelete={setDeletingTaskId}
                        onCancelDelete={() => setDeletingTaskId(null)}
                        onDeleteTask={handleDeleteTask}
                        onPageChange={setCurrentPage}
                        renderCalendlyPopover={renderCalendlyPopover}
                        renderDurationPopover={renderDurationPopover}
                        renderUserCell={renderUserCell}
                        renderUserSelectPopover={renderUserSelectPopover}
                    />
                ) : activeTab === 'tablero' ? (
                    /* TABLERO KANBAN INTERACTIVO */
                    <TaskKanbanView
                        tasks={standardTasks}
                        systemUsers={systemUsers}
                        userTz={userTz}
                        scrollRef={kanbanScrollRef}
                        onColDragOver={handleKanbanDragOver}
                        onColDrop={handleKanbanDrop}
                        onCardDragStart={handleKanbanDragStart}
                        onCardDragEnd={handleKanbanDragEnd}
                        onCardClick={handleTaskClick}
                    />
                ) : activeTab === 'flow' ? (
                    <ComponentTaskFlow ref={flowRef} userTz={userTz} tasks={tasks} activeTaskId={activeTask?.id} systemUsers={systemUsers} showTriggersMenu={showRoadmapTriggers} onTaskClick={handleRoadmapTaskSelect} onTaskOpen={handleTaskClick} spaceId={activeSpaceId} onNodesChange={setAvailableTriggers} onTaskDrop={handleRoadmapTaskDrop} onTaskResize={handleRoadmapTaskResize} />
                ) : null}
            </div>
        </div>
        )}
            {standaloneMode && standaloneError && !activeTask && (
                <StandaloneTaskError error={standaloneError} onBackToHome={() => navigate('/')} />
            )}
            {shareModalOpen && activeTask && createPortal(
                <ShareTaskModal task={activeTask} onClose={() => setShareModalOpen(false)} />,
                document.body
            )}
            {(activeTask || queryWorkframeId) && (() => {
                if (!activeTask) {
                    return (
                        <div className={`task-detail-panel-wrapper open`}>
                            <div className="task-panel-container with-workframe" style={{ width: '100%', height: '100%', border: 'none', background: 'transparent' }}>
                                <TaskErrorBoundary>
                                    <TaskWorkframePanel
                                        workframeId={queryWorkframeId}
                                        spaceId={activeSpaceId}
                                        onSelectTask={handleTaskClick}
                                        onCreateTask={(data) => createTaskMutation.mutateAsync({ ...data, space_id: activeSpaceId })}
                                        toast={toast}
                                    >
                                        <div className="wf-empty-detail-placeholder" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '400px', flexShrink: 0, color: 'rgba(255,255,255,0.4)', fontSize: '14px', borderLeft: '1px solid rgba(255,255,255,0.05)' }}>
                                            {translate('workframe.selectTaskToViewDetails', { defaultValue: 'Selecciona una actualización en la bitácora para ver sus detalles' })}
                                        </div>
                                    </TaskWorkframePanel>
                                </TaskErrorBoundary>
                            </div>
                        </div>
                    );
                }

                const wfSlots = taskWorkframeSlots({
                    activeTask,
                    enabled: true,
                    tasks,
                    onUpdateTask: updateTaskInDB,
                    onOpenTask: handleTaskClick,
                    onCreateUpdate: createWorkframeUpdate,
                    onOpenCreateComment: enterSuggestionMode,
                    toast,
                });
                return (
                <TaskDetailPanel
                    activeTask={activeTask}
                    portalTarget={portalTarget}
                    standaloneMode={standaloneMode}
                    standaloneTaskId={standaloneTaskId}
                    propStandaloneTaskId={propStandaloneTaskId}
                    onPrevTask={onPrevTask}
                    onNextTask={onNextTask}
                    {...wfSlots}
                    isTagsCollapsed={isTagsCollapsed}
                    onToggleCollapse={() => {
                        const willCollapse = !isTagsCollapsed;
                        setIsTagsCollapsed(willCollapse);
                        if (willCollapse) {
                            setTaskCommentsTab('info');
                        } else if (taskCommentsTab === 'info') {
                            setTaskCommentsTab('comentarios');
                        }
                    }}
                    onClose={onCloseStandalone || (() => navigate(basePath))}
                    onBackToHome={() => navigate('/')}
                    onTitleChange={(val) => {
                        if (titleTimeoutRef.current) clearTimeout(titleTimeoutRef.current);
                        titleTimeoutRef.current = setTimeout(() => {
                            updateTaskInDB(activeTask.id, { title: val });
                        }, 800);
                    }}
                    onTitleBlur={(val) => {
                        if (titleTimeoutRef.current) clearTimeout(titleTimeoutRef.current);
                        updateTaskInDB(activeTask.id, { title: val });
                    }}
                    tagsRow={
                        <TaskTagsRow
                            activeTask={activeTask}
                            dateConflictMessage={dateConflictTaskId === activeTask.id ? dateConflictMessage : null}
                            workframeDropdown={wfSlots.headerExtra}
                            onShare={() => setShareModalOpen(true)}
                            canEditTask={canEditActiveTask}
                            canRescheduleTask={canRescheduleActiveTask}
                            canDeleteTask={canDeleteActiveTask}
                            spaceName={activeTaskSpaceName}
                            userSpaces={userSpaces}
                            onAssignSpace={(space) => {
                                updateTaskInDB(activeTask.id, { space_id: space.id });
                                setSpaceNameCache(prev => ({ ...prev, [space.id]: space.name }));
                            }}
                            systemUsers={systemUsers}
                            usersToSelectFrom={usersToSelectFrom}
                            activeUserSelectTaskId={activeUserSelectTaskId}
                            activeUserSelectField={activeUserSelectField}
                            onOpenUserSelect={handleOpenUserSelect}
                            onToggleUser={handleToggleUserSelect}
                            userSearchQuery={userSearchQuery}
                            onUserSearchChange={setUserSearchQuery}
                            focusedUserOptionIdx={focusedUserOptionIdx}
                            onFocusedIdxChange={setFocusedUserOptionIdx}
                            userSelectPopoverRef={userSelectPopoverRef}
                            activeCalendlyPopoverTaskId={activeCalendlyPopoverTaskId}
                            onOpenCalendly={handleOpenCalendlyPopover}
                            popoverDurationVal={popoverDurationVal}
                            popoverDate={popoverDate}
                            popoverMonth={popoverMonth}
                            popoverYear={popoverYear}
                            onPrevMonth={handlePrevMonth}
                            onNextMonth={handleNextMonth}
                            onCalendlyTimeSelect={(taskId, t) => { setPopoverDurationVal(t); handleSaveCalendlyDetails(taskId, t); }}
                            onCalendlyDateSelect={(taskId, d) => { setPopoverDate(d); handleSaveCalendlyDetails(taskId, null, d); }}
                            activeDurationPopoverTaskId={activeDurationPopoverTaskId}
                            onOpenDuration={(taskId) => setActiveDurationPopoverTaskId(taskId)}
                            durationPopoverRef={durationPopoverRef}
                            customTimeNum={customTimeNum}
                            customTimeUnit={customTimeUnit}
                            onCustomTimeNumChange={setCustomTimeNum}
                            onCustomTimeUnitChange={setCustomTimeUnit}
                            onCustomTimeSubmit={handleCustomTimeSubmit}
                            onPresetSelect={(taskId, preset) => { handleTimeSelect(taskId, preset); setActiveDurationPopoverTaskId(null); }}
                            onChangeStage={(taskId, stage) => updateTaskInDB(taskId, { stage })}
                            onSaveRecurrence={(taskId, interval, unit) => updateTaskInDB(taskId, { recurrent_interval: interval, recurrent_unit: unit })}
                            onAddTag={handleAddTag}
                            onRemoveTag={handleRemoveTag}
                            dateFlash={dateFlash}
                            onAutoSchedule={(taskId, durationMinutes) => {
                                setDateFlash(true);
                                setTimeout(() => setDateFlash(false), 2000);
                                updateTaskInDB(taskId, { autoSchedule: true, duration_minutes: durationMinutes });
                            }}
                            onDeleteTaskConfirm={() => {
                                toast.prompt(translate('modal.deleteConfirm'), {
                                    confirmText: translate('actions.delete'),
                                    onConfirm: () => handleDeleteTask(activeTask.id),
                                });
                            }}
                        />
                    }
                    infoContent={renderInfoContent()}
                    commentsPanel={
                        <TaskCommentsPanel
                            isTagsCollapsed={isTagsCollapsed}
                            activeTab={taskCommentsTab}
                            onTabChange={setTaskCommentsTab}
                            listRef={commentsListRef}
                            infoContent={renderInfoContent()}
                            comments={taskFilteredComments}
                            currentUserId={user?.id}
                            editingCommentId={editingCommentId}
                            expandedCommentEditor={expandedCommentEditor}
                            editDraftContent={editDraftContent}
                            onEditContentChange={setEditDraftContent}
                            onStartEdit={setEditingCommentId}
                            onSaveEdit={(commentId, html) => {
                                saveEditedComment(commentId, html);
                                setEditingCommentId(null);
                                setEditDraftContent('');
                            }}
                            onCancelEdit={() => {
                                setEditingCommentId(null);
                                setEditDraftContent('');
                            }}
                            onExpandEditEditor={() => setExpandedCommentEditor('edit')}
                            onToggleReaction={toggleCommentReaction}
                            onDeleteComment={deleteComment}
                            onToggleMediaVisibility={handleToggleMediaVisibility}
                            onExpandImage={setFullScreenImage}
                            hoveredReminderId={hoveredReminderId}
                            hoveredReminderData={hoveredReminderData}
                            reminderTooltipPos={reminderTooltipPos}
                            onReminderMouseEnter={handleReminderMouseEnter}
                            onReminderMouseLeave={handleReminderMouseLeave}
                            onReminderClick={handleReminderClick}
                            activeReminderCommentId={activeReminderCommentId}
                            onOpenReminderPopover={(commentId, e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setReminderPopoverPos({
                                    bottom: window.innerHeight - rect.top + 8,
                                    right: window.innerWidth - rect.right
                                });
                                setActiveReminderCommentId(commentId);
                                setShowReminderPopover(!showReminderPopover);
                            }}
                            onCloseReminderPopover={() => setActiveReminderCommentId(null)}
                            renderReminderPopover={renderReminderPopover}
                            canTagUsers={getTaskPermissions(activeTask?.space_id)?.task_tag !== false}
                            usersToSelectFrom={usersToSelectFrom}
                            userId={user?.dbId || user?.id}
                            editorTranslate={translate}
                            taskSmartTags={taskSmartTags}
                            onSmartTagsChange={setTaskSmartTags}
                            pendingReminder={pendingReminder}
                            canComment={getTaskPermissions(activeTask?.space_id)?.task_comment !== false}
                            createDraftContent={createDraftContent}
                            createResetKey={createResetKey}
                            onCreateContentChange={setCreateDraftContent}
                            onSubmitComment={submitTaskComment}
                            onExpandCreateEditor={() => setExpandedCommentEditor('create')}
                            showReminderPopover={showReminderPopover}
                            onCloseCommentReminderPopover={() => setShowReminderPopover(false)}
                            suggestionMode={suggestionMode}
                            onExitSuggestionMode={() => setSuggestionMode(false)}
                            onEnterSuggestionMode={enterSuggestionMode}
                            taskMode={taskMode}
                            onExitTaskMode={() => setTaskMode(false)}
                            onEnterTaskMode={enterTaskMode}
                            onMentionSelect={workframeDocSharing.onMentionSelect}
                            activeTask={activeTask}
                            user={user}
                        />
                    }
                    commentEditorModals={
                        <TaskCommentEditorModals
                            expandedCommentEditor={expandedCommentEditor}
                            onClose={() => setExpandedCommentEditor(null)}
                            translate={translate}
                            canComment={getTaskPermissions(activeTask?.space_id)?.task_comment !== false}
                            canTagUsers={getTaskPermissions(activeTask?.space_id)?.task_tag !== false}
                            usersToSelectFrom={usersToSelectFrom}
                            userId={user?.dbId || user?.id}
                            createDraftContent={createDraftContent}
                            createResetKey={createResetKey}
                            onCreateContentChange={setCreateDraftContent}
                            onSubmitComment={submitTaskComment}
                            onSmartTagsChange={setTaskSmartTags}
                            onMentionSelect={workframeDocSharing.onMentionSelect}
                            editDraftContent={editDraftContent}
                            editingCommentId={editingCommentId}
                            editingCommentContent={taskComments.find(c => c.comment_id === editingCommentId)?.content}
                            onEditContentChange={setEditDraftContent}
                            onSaveEdit={(html) => {
                                saveEditedComment(editingCommentId, html);
                                setEditingCommentId(null);
                                setEditDraftContent('');
                                setExpandedCommentEditor(null);
                            }}
                            onCancelEdit={() => {
                                setEditingCommentId(null);
                                setEditDraftContent('');
                                setExpandedCommentEditor(null);
                            }}
                        />
                    }
                />
                );
            })()}

            {/* Full Screen Image Viewer Modal */}
            <FullScreenImageViewer image={fullScreenImage} onClose={() => setFullScreenImage(null)} />

            <WorkframeSharePromptModal {...workframeDocSharing.modalProps} />

        <CalendarEventOverlay
            event={activeCalendarEvent}
            portalTarget={portalTarget}
            user={user}
            onClose={handleCloseCalendarEvent}
            onSave={handleSaveCalendarEvent}
            onDelete={handleDeleteCalendarEvent}
        />

        {bottomLeftTarget && createPortal(
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ color: '#a0a0a0', fontSize: '11px', fontWeight: 500, letterSpacing: '0.5px' }}>
                    {taskMetrics.percentage}% tareas hechas
                </span>
                <div style={{ width: '200px', height: '4px', backgroundColor: '#333', borderRadius: 'var(--radius-sm)', overflow: 'hidden' }}>
                    <div style={{ width: `${taskMetrics.percentage}%`, height: '100%', backgroundColor: '#4ade80', transition: 'width 0.4s ease' }} />
                </div>
            </div>,
            bottomLeftTarget
        )}
        </>
        </AccessGate>
    );
};

// Modal de compartir tarea (`.claude/plans/task-share-link.md`) -- versión simplificada del
// share de YouTube: link session-independent (`/t/:taskId`, resuelve para cualquier miembro
// del space vía el chequeo de `GetTask` en el backend) + copiar + 3 accesos directos. Sin
// "Embed"/"Create post"/analytics, a propósito (pedido explícito del usuario).
export default ComponentTaskHoy;
